import { Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

// Grand-total metric value (rollup grouping 0), with the API's -1 sentinel mapped to null.
export function readGrandTotal(rollups: AnalyticsResponseRollupCollection, metric: Metrics): number | null {
  const total = rollups.groupings[0];
  if (!total) return null;
  const value = total.metrics.getCurrentSeries(metric)[0];
  return value != null && value !== -1 ? value : null;
}

// Same as readGrandTotal but defaulting null to 0 — for count metrics that should never be null in UI.
export function readGrandTotalOrZero(rollups: AnalyticsResponseRollupCollection, metric: Metrics): number {
  return readGrandTotal(rollups, metric) ?? 0;
}

export function rateToCount(rate: number | null, total: number): number {
  return rate != null ? Math.round((rate / 100) * total) : 0;
}

// Combined read used by every metric-detail header: total referrals + a rate + the implied count.
export function readReferralRate(
  rollups: AnalyticsResponseRollupCollection,
  rateMetric: Metrics
): { rate: number | null; count: number; total: number } {
  const total = readGrandTotalOrZero(rollups, Metrics.REVIEW_ID_COUNT);
  const rate = readGrandTotal(rollups, rateMetric);
  return { rate, count: rateToCount(rate, total), total };
}

// Zip dimension names + counts, drop empty / 'None' / zero-count rows, optionally remap display names,
// optionally sort by count desc. Used by the per-metric breakdown charts.
export function buildBreakdownRows(
  names: ReadonlyArray<string | null | undefined>,
  counts: ReadonlyArray<number | null | undefined>,
  options: { displayNameMap?: Record<string, string>; lowercase?: boolean; sortDesc?: boolean } = {}
): Array<{ name: string; count: number }> {
  const { displayNameMap, lowercase, sortDesc } = options;
  const rows = names
    .map((raw, i) => {
      const key = String(raw ?? '');
      const display = displayNameMap?.[key] ?? key;
      return { name: lowercase ? display.toLowerCase() : display, count: counts[i] ?? 0 };
    })
    .filter((r) => r.name && r.name.toLowerCase() !== 'none' && r.count > 0);
  if (sortDesc) rows.sort((a, b) => b.count - a.count);
  return rows;
}
